import React from 'react';
import { MdMenu, MdClose, MdKeyboard } from 'react-icons/md';
import ProgressPill from '../ProgressPill';

const clamp = (n, min, max) => Math.min(max, Math.max(min, n));
const clamp01 = (x) => clamp(x, 0, 1);

const formatMMSS = (totalSeconds) => {
  const s = Math.max(0, Math.floor(Number(totalSeconds) || 0));
  const mm = String(Math.floor(s / 60));
  const ss = String(s % 60).padStart(2, "0");
  return `${mm}:${ss}`;
};

function MobileProgressStrip({
  doneSeconds = 0,
  goalSeconds = 60,
  onClick,
  languageFlag = null,
}) {
  const safeGoal = Math.max(1, Number(goalSeconds) || 60);
  const safeDone = Math.max(0, Number(doneSeconds) || 0);

  const ratio = safeDone / safeGoal;
  const pct = Math.round(clamp01(ratio) * 100);

  const timeText = `${formatMMSS(safeDone)} / ${formatMMSS(safeGoal)}`;

  return (
    <button
      type="button"
      onClick={typeof onClick === "function" ? onClick : undefined}
      className={[
        "w-full text-left",
        "px-3 py-2",
        "rounded-xl",
        "bg-white/60 hover:bg-white/75",
        "border border-white/40",
        "backdrop-blur-sm",
        "transition",
        "active:scale-[0.99]",
      ].join(" ")}
    >
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 text-xs font-medium text-gray-800">
            <span className="inline-flex items-center justify-center w-6 h-6 rounded-lg bg-gray-100">
              🏆
            </span>
            <span className="truncate">Daily</span>
            {languageFlag ? <span className="opacity-70">{languageFlag}</span> : null}
          </div>
          <div className="text-[11px] text-gray-500 mt-0.5">{timeText}</div>
        </div>

        <div className="flex items-center gap-2 flex-shrink-0">
          <div className="text-xs font-semibold text-gray-700">{pct}%</div>
        </div>
      </div>

      <div className="mt-2 h-1.5 w-full rounded-full bg-gray-200 overflow-hidden">
        <div
          className="h-full rounded-full"
          style={{
            width: `${pct}%`,
            background: "linear-gradient(to right, #34d399, #10b981)",
            transition: "width 0.25s ease",
          }}
        />
      </div>
    </button>
  );
}


export const MobileHeader = ({
  language,
  mobileMenuOpen,
  setMobileMenuOpen,
  setShowKeyboardHelp,
  showIntonationVisualization,
  setShowIntonationVisualization,
  visualizationView,
  setVisualizationView,
  translation,
  setTranslation,
  practiceMode,
  setPracticeMode,
  cancelAllProcesses,
  setPreservedMode,

  // ProgressPill props (optional for guests)
  todaySeconds,
  goalSeconds,
  setShowProgressModal,
  isPublic = false,
}) => {
  const hasProgress =
    !isPublic &&
    typeof todaySeconds === 'number' &&
    typeof goalSeconds === 'number' &&
    typeof setShowProgressModal === 'function';

  const toggleMenu = () => setMobileMenuOpen(!mobileMenuOpen);
  const closeMenu = () => setMobileMenuOpen(false);

  const openKeyboardHelp = () => {
    setShowKeyboardHelp(true);
    closeMenu();
  };

  const openProgress = () => {
    if (!hasProgress) return;
    setShowProgressModal(true);
    closeMenu();
  };

  return (
    <>
      {/* MOBILE TOP BAR */}
      <div className="xl:hidden flex items-center justify-between p-4 bg-white/80 backdrop-blur-sm border-b border-white/30 sticky top-0 z-40">
        <button
          onClick={toggleMenu}
        
          className="hamburger-button p-2 hover:bg-white/60 rounded-xl transition-all duration-200 border border-white/40 shadow-sm"
          aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
        >
          {mobileMenuOpen ? (
            <MdClose className="text-gray-600 text-xl" />
          ) : (
            <MdMenu className="text-gray-600 text-xl" />
          )}
        </button>

        {/* ProgressPill in header - hidden for guests */}
        <div className="flex-1 mx-3">
  {hasProgress ? (
    <MobileProgressStrip
      doneSeconds={todaySeconds}
      goalSeconds={goalSeconds}
      onClick={openProgress}
      languageFlag={language ? null : null} // optional, see note below
    />
  ) : (
    <div className="h-10" />
  )}
</div>


        
      </div>

      {/* MOBILE MENU OVERLAY */}
      {mobileMenuOpen && (
        <div className="mobile-menu fixed inset-0 z-50 xl:hidden">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/20 backdrop-blur-sm"
            onClick={closeMenu}
          />

          {/* Menu Panel */}
          <div className="absolute top-0 left-0 w-80 h-full bg-white/95 backdrop-blur-sm border-r border-white/30 shadow-xl animate-in slide-in-from-left-1 duration-300">
            <div className="p-6 h-full overflow-y-auto">
              {/* Header */}
              <div className="flex items-center justify-between mb-8">
                <h2 className="font-bold text-gray-800 text-xl flex items-center gap-2">
                  <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-lg flex items-center justify-center text-white text-sm">
                    🎵
                  </div>
                  Language Practice
                </h2>
                <button
                  onClick={closeMenu}
                  className="p-2 hover:bg-gray-100 rounded-xl transition-all duration-200"
                  aria-label="Close menu"
                >
                  <MdClose className="text-gray-500 text-xl" />
                </button>
              </div>

              {/* Practice Mode Section */}
              <div className="bg-white/60 rounded-2xl p-4 border border-gray-200 mb-6">
                <h3 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
                  <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-lg flex items-center justify-center text-white text-sm">
                    🎵
                  </div>
                  Practice Mode
                </h3>

                <div className="space-y-3">
                  {[
                    {
                      key: 'echoing',
                      label: 'Echo Mode',
                      icon: '🔄',
                      desc: 'Listen → Repeat',
                      activeClass: 'bg-gradient-to-r from-blue-500 to-indigo-500',
                    },
                    {
                      key: 'writeEcho',
                      label: 'Write & Echo',
                      icon: '✍️',
                      desc: 'Listen → Write → Repeat',
                      activeClass: 'bg-gradient-to-r from-violet-500 to-indigo-600',
                    },
                    {
                      key: 'shadowing',
                      label: 'Shadow Mode',
                      icon: '🎭',
                      desc: 'Speak with narrator',
                      activeClass: 'bg-gradient-to-r from-amber-500 to-orange-500',
                    },
                    {
                      key: 'manual',
                      label: 'Manual Mode',
                      icon: '🎮',
                      desc: 'Manual control',
                      activeClass: 'bg-gradient-to-r from-green-500 to-emerald-500',
                    },
                  ].map(({ key, label, icon, desc, activeClass }) => (
                    <button
                      key={key}
                      onClick={() => {
                        cancelAllProcesses();
                        setPracticeMode(key);
                        setPreservedMode(key);
                        closeMenu();
                      }}
                      className={`w-full text-left py-3 px-4 rounded-xl transition-all duration-300 font-medium text-sm flex items-center justify-between group border ${
                        practiceMode === key
                          ? `${activeClass} text-white shadow-lg border-transparent`
                          : 'bg-white/60 text-gray-600 hover:bg-white/80 border border-white/40 hover:border-white/60 hover:shadow-md'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <div
                          className={`w-8 h-8 rounded-lg flex items-center justify-center text-sm transition-all duration-300 ${
                            practiceMode === key ? 'bg-white/20' : 'bg-gray-100 group-hover:bg-gray-200'
                          }`}
                        >
                          {icon}
                        </div>
                        <div className="text-left">
                          <div className="font-medium">{label}</div>
                          <div className={`text-xs ${practiceMode === key ? 'text-white/80' : 'text-gray-500'}`}>
                            {desc}
                          </div>
                        </div>
                      </div>

                      {practiceMode === key && <div className="w-2 h-2 bg-white rounded-full animate-pulse" />}
                    </button>
                  ))}
                </div>
              </div>

              {/* Visualization Controls */}
              <div className="bg-white/60 rounded-2xl p-4 border border-gray-200 mb-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-semibold text-gray-800 flex items-center gap-2">
                    <div className="w-8 h-8 bg-gradient-to-br from-purple-500 to-pink-500 rounded-lg flex items-center justify-center text-white text-sm">
                      📊
                    </div>
                    Visualization
                  </h3>

                  <button
                    onClick={() => setShowIntonationVisualization(!showIntonationVisualization)}
                    className={`relative inline-flex h-7 w-12 items-center rounded-full transition-all duration-300 ${
                      showIntonationVisualization
                        ? 'bg-gradient-to-r from-green-500 to-emerald-500'
                        : 'bg-gray-300'
                    }`}
                    aria-label="Toggle visualization"
                  >
                    <span
                      className={`inline-block h-5 w-5 transform rounded-full bg-white shadow-lg transition-all duration-300 ${
                        showIntonationVisualization ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </div>

                {showIntonationVisualization && (
                  <div className="space-y-3">
                    <h4 className="font-medium text-gray-700 text-sm">View Type</h4>
                    <div className="grid grid-cols-2 gap-2">
                      {[
                        { key: 'pitch', icon: '📈', label: 'Pitch' },
                        { key: 'intensity', icon: '🔊', label: 'Intensity' },
                        { key: 'music', icon: '🎵', label: 'Music' },
                        { key: 'colors', icon: '🎨', label: 'Colors' },
                        { key: 'combined', icon: '🌀', label: 'Combined', colSpan: true },
                      ].map(({ key, icon, label, colSpan }) => (
                        <button
                          key={key}
                          onClick={() => {
                            setVisualizationView(key);
                            closeMenu();
                          }}
                          className={`p-3 rounded-xl transition-all duration-300 flex items-center gap-2 text-sm ${
                            visualizationView === key
                              ? 'bg-gradient-to-r from-blue-500 to-indigo-500 text-white shadow-lg'
                              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                          } ${colSpan ? 'col-span-2 justify-center' : ''}`}
                        >
                          <span>{icon}</span>
                          <span>{label}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              
            </div>
          </div>
        </div>
      )}
    </>
  );
};